import { useLayoutEffect } from 'react';

/**
 * useAutoFitScale — PPTX-style canvas scaling.
 * Measures the inner canvas and uniformly scales it so it fills the
 * outer viewport box to the absolute maximum without cropping:
 *   scale = min(availW / needW, availH / needH)
 * Upscales small content (fills the screen like PowerPoint) and
 * downscales overflowing content (nothing is ever cut off).
 */
export default function useAutoFitScale(outerRef, innerRef, { disabled = false } = {}) {
  useLayoutEffect(() => {
    if (disabled) return undefined;
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;

    const fit = () => {
      inner.style.transform = 'none';
      inner.style.height = '';
      inner.style.width = '';
      const rect = outer.getBoundingClientRect();
      const cs = window.getComputedStyle(outer);
      const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const availH = Math.min(rect.height, window.innerHeight - Math.max(0, rect.top)) - padY;
      const availW = rect.width - padX;
      const measure = () => {
        const needH = inner.scrollHeight;
        const needW = inner.offsetWidth;
        if (availH <= 0 || !needH || !needW) return null;
        return Math.min(availW / needW, availH / needH);
      };
      let s = measure();
      if (s == null) return;
      // Headline-Kompensation: --fit-inv skaliert die Folien-Headline
      // invers zum Fit-Scale, damit sie auf JEDER Folie gleich groß
      // erscheint. Danach neu messen (Headline ändert die Höhe).
      inner.style.setProperty('--fit-inv', (1 / s).toFixed(4));
      const s2 = measure();
      if (s2 != null) {
        s = s2;
        inner.style.setProperty('--fit-inv', (1 / s).toFixed(4));
      }
      inner.style.transform = Math.abs(s - 1) > 0.002 ? `scale(${s})` : 'none';
      // 100vw × 100vh ohne Ausnahmen: der (skalierte) Canvas wird exakt so
      // breit UND hoch wie der Bildschirm — Inhalt streckt sich in beide
      // Richtungen, keine seitlichen Ränder mehr.
      inner.style.height = `${Math.round(availH / s)}px`;
      inner.style.width = `${Math.round(availW / s)}px`;
    };

    let raf = null;
    const requestFit = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fit);
    };

    fit();
    const ro = new ResizeObserver(requestFit);
    ro.observe(outer);
    // Content changes (fragments, lazy media) — attributes are NOT observed,
    // so our own style writes on `inner` cannot cause a feedback loop.
    const mo = new MutationObserver(requestFit);
    mo.observe(inner, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-fragments-remaining'],
    });
    window.addEventListener('resize', requestFit);
    const timers = [120, 400, 900, 1800].map((t) => setTimeout(fit, t));
    return () => {
      ro.disconnect();
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', requestFit);
      timers.forEach(clearTimeout);
    };
  }, [outerRef, innerRef, disabled]);
}
